import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { TextArea } from '@common/ui/inputs/TextArea';
import { CellSelect } from '@common/ui/selection';
import { Heading } from '@common/ui/typography/Heading';
import { useEventEmit } from '@common/utils/events';
import { useRequestSubmit } from '@common/utils/requestSubmitPolyfill';

import { MAX_REPORT_DESCRIPTION_LENGTH } from '@entities/dating/constants';
import { reportReasonsDict } from '@entities/dating/dicts/reportReasons';

import { usePostReport } from '@services/hooks/ReportsService';

const formDataSchema = z.object({
  reason: z.string(),
  additional: z
    .string()
    .max(
      MAX_REPORT_DESCRIPTION_LENGTH,
      `Максимальная длина ${MAX_REPORT_DESCRIPTION_LENGTH} символов`,
    )
    .optional(),
});

type FormData = z.infer<typeof formDataSchema>;

export type ReportProps = {
  profileId: string;
  type: 'chat' | 'feed';
  matchId?: string;
};

export const Report: React.FC<ReportProps> = ({
  profileId,
  type = 'feed',
  matchId,
}) => {
  const navigate = useNavigate();
  const tabbar = useTabbar();
  const emit = useEventEmit();
  const router = useRouter();

  const formRef = useRef<HTMLFormElement>(null);
  const requestSubmit = useRequestSubmit(formRef);

  const [currentStep, setCurrentStep] = useState(0);
  const [isShowError, setIsShowError] = useState(false);

  const { trigger: triggerPostReport, isMutating: isMutatingPostReport } =
    usePostReport();

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(formDataSchema),
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      // Use different API based on report type
      if (type === 'chat' && matchId) {
        // Chat report: POST /matches/:matchId/report
        await triggerPostReport({
          matchId: matchId,
          reason: data.reason,
          details: data.additional,
        });
      } else {
        // Profile report: POST /report
        await triggerPostReport({
          reported_user_id: profileId,
          reason: data.reason,
          description: data.additional,
        });
      }

      emit('success', {
        title: 'Жалоба отправлена',
        description: 'Мы разберёмся в ситуации и примем решение',
      });
    } catch (error: unknown) {
      if (error instanceof Error && error.message.includes('404')) {
        emit('error', {
          title: 'Ошибка',
          description: 'Не удалось отправить жалобу',
        });
      } else {
        emit('info', {
          title: 'Жалоба уже отправлена',
          description: 'Ещё раз пожаловаться можно через сутки',
        });
      }
    }

    if (type === 'chat') {
      const backToMatchId = matchId ?? profileId; // fallback for safety
      router.history.push(`/chats/${backToMatchId}`);
    } else if (type === 'feed') {
      navigate({ to: '/dating' });
    }
  });

  const handleChangeStep = () => {
    if (isValid) {
      setIsShowError(false);
      setCurrentStep(1);
    } else {
      setIsShowError(true);
    }
  };

  useEffect(() => {
    if (currentStep === 0) {
      tabbar.setBottomSheet(
        <BottomSheet
          buttons={[
            <Button testId="report-next-button" onClick={handleChangeStep}>
              Далее
            </Button>,
          ]}
        />,
      );
    } else {
      tabbar.setBottomSheet(
        <BottomSheet
          buttons={[
            <Button
              testId="report-submit-button"
              loading={isMutatingPostReport}
              onClick={() => requestSubmit()}
            >
              Отправить
            </Button>,
          ]}
        />,
      );
    }
  }, [isMutatingPostReport, currentStep, isValid]);

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        minHeight: 'calc(100vh - 32px)',
      }}
    >
      {currentStep === 0 && (
        <>
          <Heading level={3} noPadding style={{ margin: '16px 0' }}>
            Выберите причину
          </Heading>
          <Controller
            name="reason"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CellSelect
                value={value}
                items={Object.entries(reportReasonsDict).map(
                  ([value, label]) => ({
                    value,
                    label,
                  }),
                )}
                onChange={(value) => {
                  setIsShowError(false);
                  onChange(value);
                }}
              />
            )}
          />

          {isShowError && (
            <span
              style={{
                color: '#F53C14',
                fontSize: '12px',
                display: 'block',
                marginTop: '16px',
              }}
            >
              Необходимо выбрать причину
            </span>
          )}
        </>
      )}

      {currentStep === 1 && (
        <>
          <Heading level={3} noPadding style={{ margin: '16px 0' }}>
            Напишите причину
          </Heading>
          <Controller
            name="additional"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextArea
                placeholder="Коротко и по делу"
                description={`${value?.length || 0}/${MAX_REPORT_DESCRIPTION_LENGTH}`}
                error={errors.additional?.message}
                onChange={(value) => onChange(value || undefined)}
                maxLength={MAX_REPORT_DESCRIPTION_LENGTH}
              />
            )}
          />
        </>
      )}
    </form>
  );
};
